import React, { useState, useEffect } from "react";
import { useUser } from "@clerk/clerk-react";
import { Link } from "react-router-dom";
import { getProgressSummary, findProgressByEmail, migrateUserProgress, getAllUserIds, getAllProgressData } from "../data/database";

function DashboardPage() {
  const { user } = useUser();
  const [progress, setProgress] = useState({
    alphabet: 0,
    words: 0,
    numbers: 0,
    days: 0,
    colors: 0,
    animals: 0,
    conversation: 0
  });

  // Function to update progress from database
  const updateProgress = async () => {
    try {
      if (user) {
        console.log('Updating dashboard progress for user:', user.id, 'email:', user.primaryEmailAddress?.emailAddress);
        
        // First, try to get progress by current user ID
        let progressData = getProgressSummary(user.id);
        
        // If no progress found and user has email, try to find by email
        if (progressData.alphabet === 0 && progressData.words === 0 && progressData.numbers === 0 && 
            progressData.days === 0 && progressData.colors === 0 && progressData.animals === 0 && 
            progressData.conversation === 0 && user.primaryEmailAddress?.emailAddress) {
          
          const existingProgress = findProgressByEmail(user.primaryEmailAddress.emailAddress);
          if (existingProgress) {
            console.log('Found existing progress by email, migrating...');
            migrateUserProgress(existingProgress.userId, user.id);
            progressData = getProgressSummary(user.id);
          }
        }
        
        setProgress(progressData);
        console.log('Updating dashboard progress from database:', progressData);
      }
    } catch (e) {
      console.error('Failed to refresh progress:', e);
    }
  };

  // Update progress when user data changes
  useEffect(() => {
    if (user) {
      console.log('User data changed, updating progress');
      updateProgress();
    }
  }, [user]);

  // Refresh on visibility change to capture latest progress when returning to tab
  useEffect(() => {
    const onFocus = () => {
      if (user) updateProgress();
    };
    window.addEventListener('visibilitychange', onFocus);
    window.addEventListener('focus', onFocus);
    return () => {
      window.removeEventListener('visibilitychange', onFocus);
      window.removeEventListener('focus', onFocus);
    };
  }, [user]);

  const lessons = [
    { 
      id: 'alphabet', 
      name: 'Arabic Alphabet', 
      total: 28, 
      completed: progress.alphabet || 0,
      link: '/alphabet-lesson'
    },
    { 
      id: 'words', 
      name: 'Basic Words', 
      total: 16, 
      completed: progress.words || 0,
      link: '/word-lesson'
    },
    { 
      id: 'numbers', 
      name: 'Numbers', 
      total: 14, 
      completed: progress.numbers || 0,
      link: '/numbers-lesson'
    },
    { 
      id: 'days', 
      name: 'Days of the Week', 
      total: 7, 
      completed: progress.days || 0,
      link: '/days-lesson'
    },
    { 
      id: 'colors', 
      name: 'Colors', 
      total: 10, 
      completed: progress.colors || 0,
      link: '/colors-lesson'
    },
    { 
      id: 'animals', 
      name: 'Animals', 
      total: 10, 
      completed: progress.animals || 0,
      link: '/animals-lesson'
    },
    { 
      id: 'conversation', 
      name: 'Conversational Phrases', 
      total: 16, 
      completed: progress.conversation || 0,
      link: '/conversational-lesson'
    }
  ];

  if (!user) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#1a1a1a] to-[#2d2d2d] text-white p-4">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-3xl font-bold mb-8 text-center">Dashboard</h1>
          <div className="text-center">
            <p className="text-xl mb-4">Please sign in to view your progress</p>
            <Link
              to="/sign-in"
              className="inline-block bg-[#4CAF50] text-white px-6 py-3 rounded-lg hover:bg-[#45a049] transition-colors"
            >
              Sign In
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full bg-gradient-to-b from-[#1a1a1a] to-[#2d2d2d] text-white p-4 overflow-y-auto">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-3xl font-bold mb-8 text-center">Your Learning Progress</h1>
        
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {lessons.map((lesson) => {
            const lessonProgress = progress[lesson.id] || 0;
            const percentage = Math.round((lessonProgress / lesson.total) * 100);
            console.log(`Lesson ${lesson.id} progress:`, { lessonProgress, percentage });
            
            return (
              <Link
                key={lesson.id}
                to={lesson.link}
                className="block bg-[#2d2d2d] rounded-lg p-6 hover:bg-[#3d3d3d] transition-colors"
              >
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl font-semibold">{lesson.name}</h2>
                  <span className="text-[#4CAF50]">{percentage}%</span>
                </div>
                <div className="w-full bg-[#1a1a1a] rounded-full h-2.5">
                  <div
                    className="bg-[#4CAF50] h-2.5 rounded-full transition-all duration-500"
                    style={{ width: `${percentage}%` }}
                  ></div>
                </div>
                <p className="mt-2 text-gray-400">
                  {lessonProgress} of {lesson.total} items learned
                </p>
              </Link>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default DashboardPage; 